import './styles.scss';

import { faCalendar, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TextField } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useContext, useState } from 'react';
import InputMask from 'react-input-mask';

import { Dropdown } from '../../../../../components/dropdown/dropdown';
import { PNLContext } from '../../../../../providers/pnl-provider';
import Utils from '../../../../../utils/immersive_utils';

import CustomCalendar from './custom-calendar-pnl';
import Input from '../../../../../components/input/input';

interface CalendarProps {
  onApply: (startDate: string, endDate: string) => void;
  isSelectPeriod?: boolean;
  label?: string;
  isDateField?: boolean;
  disabled?: boolean;
}

const FORMAT_DATE = 'YYYY/MM/DD';

export function CalendarDualInteractivePnl(props: CalendarProps) {
  const { onApply, label = 'Date', isDateField = true, disabled = false } = props;
  const {
    filters: { datetime, setDatetime, datetimeStrings, setDatetimeStrings, time, setTime },
  } = useContext(PNLContext);

  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    if (disabled) return;
    setIsOpen((state) => !state);
  };

  const cleanDateTime = () => {
    setDatetime(['', '']);
    setDatetimeStrings(['', '']);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleApply = () => {
    const minDate = new Date('2019-03-20');
    if (new Date(datetime[0]) < minDate || new Date(datetime[1]) < minDate) {
      cleanDateTime();

      return;
    }

    setIsOpen(false);
    onApply(datetime[0], datetime[1]);
  };

  const convertDateString = (stringDate: string) => {
    if (stringDate.indexOf('_') >= 0) {
      return '';
    }

    const [year, month, day] = stringDate.split('-');

    const date = moment().utc();
    date.set({
      year: parseInt(year),
      month: parseInt(month) - 1,
      date: parseInt(day),
    });

    const formatDate = date.format(FORMAT_DATE);

    if (!Utils.isValidDate(new Date(formatDate))) {
      return '';
    }

    return formatDate;
  };

  const handleChangeInputDate = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const value = evt.target.value;
      setDatetimeStrings((previous: [string, string]) => {
        if (index === 0) {
          setDatetime((prevDatetime) => [convertDateString(value), prevDatetime[1]]);
          return [value, previous[1]];
        } else {
          setDatetime((prevDatetime) => [prevDatetime[0], convertDateString(value)]);
          return [previous[0], value];
        }
      });
    },
    [],
  );

  const showDateString = (date: string) => {
    return moment(date).utc(true).format('DD MMM YYYY');
  };

  const handleUpdateCalendar = (value: [string, string]) => {
    if (isDateField) {
      const startDate = moment(value[0]);
      startDate.startOf('day');

      const endDate = moment(value[1]);
      endDate.endOf('day');

      setDatetime([startDate.format(FORMAT_DATE), endDate.format(FORMAT_DATE)]);

      setDatetimeStrings([startDate.format(FORMAT_DATE), endDate.format(FORMAT_DATE)]);

      return;
    }

    setDatetime([moment(value[0]).format(FORMAT_DATE), moment(value[1]).format(FORMAT_DATE)]);

    setDatetimeStrings([
      moment(value[0]).format(FORMAT_DATE),
      moment(value[1]).format(FORMAT_DATE),
    ]);
  };

  const handleChangeTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;
    raw = raw.replace(/\D/g, '').slice(0, 2);

    if (raw === '') {
      setTime('');
      return;
    }

    const num = parseInt(raw, 10);
    if (isNaN(num)) {
      return;
    }

    if (num < 1) {
      setTime('1');
      return;
    }

    if (num > 24) {
      setTime('24');
      return;
    }

    setTime(raw);
  };

  const handleBlurTime = () => {
    if (!time || isNaN(Number(time))) {
      setTime('24');
    }
  };

  return (
    <div
      className={`calendar-dual-interactive-container ${
        isDateField ? 'calendar-interactive-field-container' : ''
      }`}
    >
      {isDateField ? (
        <>
          <span className="label">{label}</span>

          <div
            className={`calendar-select ${disabled ? 'disabled-label' : ''}`}
            onClick={handleClick}
          >
            <FontAwesomeIcon icon={faCalendar} className="icon" />
            <span className="date-label">
              {showDateString(datetime[0])} - {showDateString(datetime[1])}
            </span>
            <FontAwesomeIcon icon={faChevronDown} className="icon" />
          </div>
        </>
      ) : (
        <Button onClick={handleClick} className="calendar-btn">
          <FontAwesomeIcon icon={faCalendar} />
        </Button>
      )}

      <Dropdown isOpen={isOpen} setIsOpen={setIsOpen} classNames="dual-calendar-pnl">
        <div className="menu-date-container">
          <div className="dual-calendar-wrapper">
            <CustomCalendar
              value={datetime}
              handlerRange={handleUpdateCalendar}
              selectRange={true}
            />
            <div className="dual-calendar-wrapper-actions">
              <div className="calendar-inputs">
                <InputMask
                  mask="9999-99-99"
                  onChange={(evt) => handleChangeInputDate(evt, 0)}
                  value={datetimeStrings[0]}
                  className="input-date"
                >
                  <TextField size="small" margin="dense" type="text" sx={{ width: '60%' }} />
                </InputMask>
                <span className="calendar-inputs-date-line-separate"></span>
                <InputMask
                  mask="9999-99-99"
                  onChange={(evt) => handleChangeInputDate(evt, 1)}
                  value={datetimeStrings[1]}
                  className="input-date"
                >
                  <TextField size="small" margin="dense" type="text" sx={{ width: '60%' }} />
                </InputMask>

                <span className="calendar-inputs-time-line-separate"></span>

                <div className="calendar-input-time">
                  <span className="calendar-input-time-label">End of day:</span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    value={time}
                    onChange={handleChangeTime}
                    onBlur={handleBlurTime}
                    maxLength={2}
                    className="input-time"
                  />
                </div>
              </div>
              <div className="calendar-buttons">
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ width: '100%' }}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ width: '100%' }}
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Dropdown>
    </div>
  );
}
